import React, {
  Fragment,
  memo,
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { useSelector } from 'react-redux'
import { CollectedData } from 'src/interfaces/collectedData'
import {
  ContractorDetails,
  ContractorRecollection,
  RecollectionType,
} from 'src/interfaces/contractor'
import { CollectDataStepCreateReason } from 'src/constants/contractor/enums'
import { ContractorStepDetailsCard } from 'src/components/contractorStepDetailsCard/ContractorStepDetailsCard'
import { mapCollectedDataItemToCardStatus } from 'src/components/contractorStepDetailsCard/utils'
import {
  ContractorStepDetailsCardStatus,
  ContractorStepDetailsCardUsageVariant,
} from 'src/components/contractorStepDetailsCard/constants'
import {
  ContractorStepExpirationStatus,
  isApprovedCollectedDataStatus,
} from 'src/constants/collectedData'
import { getPartnerId } from 'src/selectors/user'
import { getContractorStepItemState } from '../contractorStepDetailsCard/hooks/useContractorStepItemState'
import { useStepContentBlocks } from '../contractorStepDetailsCard/hooks/useStepContentBlocks'
import { ContractorStepDetailsModal } from './ContractorStepDetailsModal'

export interface ContractorGroupedStepItemProps {
  collectedDataItems: CollectedData[]
  contractorDetails: ContractorDetails
  delayed?: boolean
  groupKey: string
  openModalId: string | null
  isRequestApprove?: boolean
  onRefreshContractors: () => void | Promise<void>
  setOpenModalId: Dispatch<SetStateAction<string | null>>
  isSubcontractor?: boolean
}

const ContractorGroupedStepItemComponent = ({
  collectedDataItems,
  contractorDetails,
  delayed,
  groupKey,
  openModalId,
  isRequestApprove,
  onRefreshContractors,
  setOpenModalId,
  isSubcontractor,
}: ContractorGroupedStepItemProps) => {
  const partnerId = useSelector(getPartnerId)

  const [selectedModalItemId, setSelectedModalItemId] = useState<string | null>(null)

  const modalId = `group-${groupKey}`
  const isStepDetailsModalOpen = openModalId === modalId

  const selectedCollectedData = useMemo(
    () =>
      selectedModalItemId
        ? collectedDataItems.find((item) => item.id === selectedModalItemId)
        : collectedDataItems[0],
    [collectedDataItems, selectedModalItemId],
  )

  const openModalForItem = useCallback(
    (itemId: string) => {
      setSelectedModalItemId(itemId)
      setOpenModalId(modalId)
    },
    [modalId, setOpenModalId, setSelectedModalItemId],
  )

  const closeModal = useCallback(() => {
    setOpenModalId(null)
    setSelectedModalItemId(null)
  }, [setOpenModalId, setSelectedModalItemId])

  const firstItem = collectedDataItems[0]
  const firstItemState = getContractorStepItemState({
    collectedData: firstItem,
    contractorDetails,
    isRequestApprove,
    isSubcontractor,
  })

  const isRecollecting = collectedDataItems.some((item) => item.isRecollecting)

  const recollectionData = useMemo<ContractorRecollection[]>(() => {
    const knownRecollectionReasons = Object.values(CollectDataStepCreateReason) as string[]

    const recollectingItems = collectedDataItems.filter(
      (item) =>
        item.isRecollecting &&
        knownRecollectionReasons.includes(item.collectDataStep.createReason ?? ''),
    )

    return recollectingItems.map((item) => {
      const type =
        item.collectDataStep.createReason === CollectDataStepCreateReason.AutoRecollection
          ? RecollectionType.Automatic
          : RecollectionType.Manual

      return {
        type,
        stepType: item.collectDataStep.type,
        stepLabel: item.collectDataStep.label,
        initiatorEmail: item.collectDataStep.createInitiatorEmail ?? undefined,
        createReason: item.collectDataStep.createReason ?? undefined,
        createInitiatorSource: item.collectDataStep.createInitiatorSource ?? undefined,
      }
    })
  }, [collectedDataItems])

  const hasAnyApproved = collectedDataItems.some(
    (item) =>
      isApprovedCollectedDataStatus(item.status) &&
      item.expirationStatus != null &&
      item.expirationStatus !== ContractorStepExpirationStatus.Expired,
  )
  const cardStatus = hasAnyApproved
    ? ContractorStepDetailsCardStatus.Approved
    : firstItemState.isExpired
    ? ContractorStepDetailsCardStatus.Expired
    : mapCollectedDataItemToCardStatus(firstItem, firstItemState.stepStatus)

  const isOptional = collectedDataItems.some((item) => !!item.collectDataStep.optional)

  const contentBlocks = useStepContentBlocks({
    collectedDataItems,
    contractorDetails,
    partnerId,
    isRequestApprove,
    isSubcontractor,
    onRefreshContractors,
    openModalForItem,
  })

  const selectedContentBlock = useMemo(
    () =>
      selectedModalItemId
        ? contentBlocks.find((block) => block.collectedData?.id === selectedModalItemId)
        : contentBlocks[0],
    [contentBlocks, selectedModalItemId],
  )

  const showDetails = contentBlocks.some((b) => b.onReviewStepDataAction != null)
  const showRecollectButton = !isRecollecting && contentBlocks.some((b) => b.showRecollectButton)

  const recollectableBlock = contentBlocks.find((b) => b.showRecollectButton)
  let collectedDataForCard: CollectedData = firstItem

  if (recollectableBlock && recollectableBlock.collectedData) {
    collectedDataForCard = recollectableBlock.collectedData
  }

  const handleReviewStepData = useCallback(() => {
    const block = contentBlocks[0]

    if (block.collectedData) {
      openModalForItem(block.collectedData.id)
    }
  }, [contentBlocks, openModalForItem])

  const onReviewStepDataAction =
    showDetails && contentBlocks.length === 1 ? handleReviewStepData : undefined

  return (
    <Fragment>
      <ContractorStepDetailsCard
        variant={ContractorStepDetailsCardUsageVariant.Contractor}
        status={cardStatus}
        isRecollecting={isRecollecting}
        recollectionData={recollectionData}
        showDelayedBanner={delayed}
        onReviewStepDataAction={onReviewStepDataAction}
        collectedData={collectedDataForCard}
        contractorDetails={contractorDetails}
        onRefreshContractors={onRefreshContractors}
        showRecollectButton={showRecollectButton}
        contentBlocks={contentBlocks}
        isOptional={isOptional}
      />

      {isStepDetailsModalOpen && selectedCollectedData && selectedContentBlock && (
        <ContractorStepDetailsModal
          collectedData={selectedCollectedData}
          contractorDetails={contractorDetails}
          delayed={delayed}
          isCombinedStep={collectedDataItems.length > 1}
          isRequestApprove={isRequestApprove}
          isSubcontractor={isSubcontractor}
          contentBlock={selectedContentBlock}
          onRefreshContractors={onRefreshContractors}
          onClose={closeModal}
        />
      )}
    </Fragment>
  )
}

export const ContractorGroupedStepItem = memo(ContractorGroupedStepItemComponent)
